import React, {useContext, useEffect, useRef, useState} from "react";
import {Cancel, Check, Close, Save} from "@mui/icons-material";
import {AppbarContentContext} from "../../../../commons/routing/appbar/AppbarContentProvider";
import AppbarActionButton from "../../../../commons/elements/buttons/AppbarActionButton";
import GeneralEditScoredSceneForm from "../utils/GeneralEditScoredSceneForm";
import {useParams} from "react-router-dom";
import useEditScoredVideo from "./useEditScoredVideo";
import useLoadScoredScene from "../utils/useLoadScoredScene";
import LoadingProgressIndication from "../../../../commons/elements/progress/loading/LoadingProgressIndication";
import LastUpdateInfo from "../../../../commons/utils/lastUpdate/LastUpdateInfo";
import {useIntl} from "react-intl";
import {useStyles} from "./EditScoredSceneSection.css";
import {IconButton} from "@mui/material";
import BasicHint from "../../../../commons/utils/hint/BasicHint";
import ScoredSceneStatusIndication from "../../../../commons/utils/status/scoredScene/ScoredSceneStatusIndication";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";

const EditScoredSceneSection = () => {
    const classes = useStyles()

    // Get keys from parameter
    const {videoKey, scoredSceneKey} = useParams()

    // The freshly loaded data based on the keys from the URL parameter
    const {selectedVideo, selectedScoredScene, loading} = useLoadScoredScene(videoKey, scoredSceneKey)

    // Hook to update the appbar
    const {setCurrentAppbarContent} = useContext(AppbarContentContext)

    // For checking if the logged-in user is allowed to change the status of the scored scene
    const {signedInUserData} = useContext(FirebaseAuthContext);

    // Hook providing the save user functionality
    const {handleSave, cancel, saveInProgress, saveAttemptInProgress, handleChangeStatus} = useEditScoredVideo(videoKey)

    // Reference, which holds will hold the user data according to the current form data (from the general user component)
    const editedScoredScene = useRef()

    // Determines if the save button should be enabled.
    const [saveButtonEnabled, setSaveButtonEnabled] = useState(false)

    // Internationalization for simple strings
    const intl = useIntl();

    // Setting the appbar content
    useEffect(() => {
        setCurrentAppbarContent({
            header: intl.formatMessage({
                id: 'app.scoredScene.edit.appbar.header',
                defaultMessage: 'Define Scene & Scores'
            }),
            description: <LastUpdateInfo metadata={selectedScoredScene?.metadata}/>,
            actions: <>
                <AppbarActionButton
                    label={intl.formatMessage({
                        id: 'app.scoredScene.edit.appbar.actions.save',
                        defaultMessage: 'Save'
                    })}
                    onClick={() => handleSave(editedScoredScene.current)}
                    disabled={!saveButtonEnabled}
                    icon={<Save/>}/>
                <AppbarActionButton
                    label={intl.formatMessage({
                        id: 'app.scoredScene.edit.appbar.actions.cancel',
                        defaultMessage: 'Cancel'
                    })}
                    onClick={() => cancel()}
                    icon={<Cancel/>}/>

                {/* Status Buttons (only rendered if not own scene + isReviewer) + Indication (including history overview) */}
                <div className={classes.row}>
                    <ScoredSceneStatusIndication statusInfo={selectedScoredScene?.statusInfo}/>

                    {signedInUserData?.isReviewer &&
                    signedInUserData?.uid !== selectedScoredScene?.metadata?.creationUid &&
                    ['draft', 'rejected'].includes(selectedScoredScene?.statusInfo?.status) &&
                    <BasicHint options={{trigger: "hover", placement: 'bottom'}}
                               hintText={intl.formatMessage({
                                   id: 'app.scoredScene.edit.appbar.actions.status.approve',
                                   defaultMessage: 'Approve'
                               })}
                               element={<IconButton
                                   onClick={() => handleChangeStatus(scoredSceneKey, selectedScoredScene?.statusInfo?.status, 'approved')}
                                   size="large">
                                   <Check className={classes.statusIcon}/>
                               </IconButton>}/>}

                    {signedInUserData?.isReviewer &&
                    signedInUserData?.uid !== selectedScoredScene?.metadata?.creationUid &&
                    ['draft', 'approved'].includes(selectedScoredScene?.statusInfo?.status) &&
                    <BasicHint options={{trigger: "hover", placement: 'bottom'}}
                               hintText={intl.formatMessage({
                                   id: 'app.scoredScene.edit.appbar.actions.status.reject',
                                   defaultMessage: 'Reject'
                               })}
                               element={<IconButton
                                   onClick={() => handleChangeStatus(scoredSceneKey, selectedScoredScene?.statusInfo?.status, 'rejected')}
                                   size="large">
                                   <Close className={classes.statusIcon}/>
                               </IconButton>}/>}
                </div>
            </>,
            progress: saveAttemptInProgress || saveInProgress
        })
    }, [saveButtonEnabled, selectedScoredScene, saveAttemptInProgress, saveInProgress])

    if (saveInProgress) {
        return <LoadingProgressIndication
            text={intl.formatMessage({
                id: 'app.scoredScene.edit.save.loading',
                defaultMessage: 'Saving Video Scene...'
            })}/>
    }

    if (loading) {
        return <LoadingProgressIndication
            text={intl.formatMessage({
                id: 'app.scoredScene.edit.loading',
                defaultMessage: 'Loading Video Scene...'
            })}/>
    } else {
        return (
            <GeneralEditScoredSceneForm selectedVideo={selectedVideo}
                                        selectedScoredScene={selectedScoredScene}
                                        editedScoredSceneRef={editedScoredScene}
                                        setSaveButtonEnabled={setSaveButtonEnabled}/>
        );
    }
}

export default EditScoredSceneSection